class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindCartUpdateEvents();
  }

  /**
   * Bind cart update events to monitor quantity changes and removals
   */
  bindCartUpdateEvents() {
    // Listen for quantity input changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="updates[]"]')) {
        this.handleQuantityChange(event.target);
      }
    });

    // Listen for remove button clicks
    document.addEventListener('click', (event) => {
      if (event.target.closest('cart-remove-button')) {
        this.handleRemoveButtonClick(event.target);
      }
    });

    // ENHANCED: Multiple event listeners for maximum reliability
    document.addEventListener('cart:updated', () => {
      this.checkCartAndClose();
    });

    // FALLBACK: Also listen for other cart events
    document.addEventListener('cart:changed', () => {
      this.checkCartAndClose();
    });

    // CRITICAL FIX: Monitor form submissions directly
    const cartForm = document.getElementById('CartDrawer-Form');
    if (cartForm) {
      cartForm.addEventListener('submit', () => {
        setTimeout(() => {
          this.checkCartAndClose();
        }, 500);
      });
    }

    // ENHANCED: Periodic state check as ultimate fallback
    this.startPeriodicStateCheck();
  }

  /**
   * Start periodic state checking as ultimate fallback
   */
  startPeriodicStateCheck() {
    if (this.stateCheckInterval) {
      clearInterval(this.stateCheckInterval);
    }
    
    this.stateCheckInterval = setInterval(() => {
      if (this.classList.contains('active')) {
        this.checkCartAndClose();
      }
    }, 2000); // Check every 2 seconds when drawer is open
  }

  /**
   * Stop periodic state checking
   */
  stopPeriodicStateCheck() {
    if (this.stateCheckInterval) {
      clearInterval(this.stateCheckInterval);
      this.stateCheckInterval = null;
    }
  }

  /**
   * Handle quantity input changes
   */
  handleQuantityChange(input) {
    const newQuantity = parseInt(input.value) || 0;
    
    if (newQuantity === 0) {
      // Quantity set to 0, check if cart will be empty after update
      setTimeout(() => {
        this.checkCartAndClose();
      }, 1000); // Wait for cart update to complete
    }
  }

  /**
   * Handle remove button clicks
   */
  handleRemoveButtonClick(button) {
    // Check current cart item count before removal
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        if (cart.item_count <= 1) {
          // This is the last item, close drawer after removal
          setTimeout(() => {
            this.close();
          }, 1000); // Wait for removal to complete
        }
        // If more than 1 item, don't close the drawer
      })
      .catch(error => {
        console.warn('Cart check failed on remove:', error);
      });
  }

  /**
   * Check cart status and close drawer if empty
   */
  checkCartAndClose() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update global cart state
        this.updateGlobalCartState(cart);
        
        if (cart.item_count === 0) {
          // Cart is empty, close the drawer
          setTimeout(() => {
            this.close();
          }, 500); // Small delay to ensure UI updates are complete
        }
      })
      .catch(error => {
        console.warn('Cart check failed:', error);
      });
  }

  /**
   * Update global cart state for synchronous access
   */
  updateGlobalCartState(cart) {
    if (window.cartState) {
      window.cartState.item_count = cart.item_count;
      window.cartState.isEmpty = cart.item_count === 0;
      window.cartState.lastUpdated = Date.now();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    
    // CRITICAL FIX: Update global cart state on manual close
    this.syncCartStateOnClose();
    
    // CLEANUP: Stop periodic checking when drawer is closed
    this.stopPeriodicStateCheck();
  }

  /**
   * Sync cart state when drawer is manually closed
   */
  syncCartStateOnClose() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.updateGlobalCartState(cart);
      })
      .catch(error => {
        // Silent fail - don't break drawer functionality
      });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.rebindEventListeners();
      this.open();
    });
  }

  rebindEventListeners() {
    // Re-bind overlay click listener
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      // Remove existing listeners to prevent duplicates
      overlay.removeEventListener('click', this.close.bind(this));
      overlay.addEventListener('click', this.close.bind(this));
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
